import { useTranslation } from 'react-i18next'

import { Field } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { Input } from '@/components/ui/Input'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { refusalMessage } from '@/features/cases/lib/refusal'
import { useDeletePayment } from '@/features/finance/api/useFinance'
import type { Payment } from '@/types/domain'

/* «Отклонить» is a DELETION with a stated cause (spec L8), asked for in a sheet
 * with a real field rather than through a browser confirm: the reason is the
 * only thing that will survive the row — it goes into the audit trail, which is
 * the one place a fact about a row that no longer exists can live — so it has
 * to be typed, and a native dialog cannot take text, cannot be translated and
 * cannot be reached by the keyboard conventions the rest of this product uses.
 *
 * ONE component for the two places the act is offered — the case's payments
 * panel and the direction page's settlements table — so the wording, the
 * mandatory-reason rule and the confirmed-write discipline cannot drift between
 * them. The recognised debt survives the deletion (the server's L8 rule); the
 * sheet does not restate that because the row the reader watches disappear is
 * the payment's, not the cost line's.
 */
export function RejectPaymentSheet({
  open,
  onClose,
  payment,
}: {
  open: boolean
  onClose: () => void
  payment: Payment
}) {
  const { t } = useTranslation()
  const reject = useDeletePayment()
  const [reason, setReason, reasonTouched, markReasonTouched] = useFormDraft(
    open ? payment.id : null,
    () => '',
  )

  const submit = () => {
    markReasonTouched()
    if (reason.trim() === '') return
    reject.mutate(
      { paymentId: payment.id, caseId: payment.caseId, reason: reason.trim() },
      { onSuccess: onClose },
    )
  }

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={t('finance.payments.rejectTitle', { reference: payment.reference })}
      submitLabel={t('finance.payments.rejectSubmit')}
      pending={reject.isPending}
      dirty={reason.trim() !== ''}
      error={
        reject.error
          ? refusalMessage(reject.error, t)
          : reasonTouched && reason.trim() === ''
            ? t('common.form.required')
            : undefined
      }
      onSubmit={submit}
    >
      <p className="text-text-muted text-[12.5px] leading-snug font-light">
        {t('finance.payments.rejectBody')}
      </p>

      <Field
        label={t('finance.payments.rejectReason')}
        required
        error={reasonTouched && reason.trim() === '' ? t('common.form.required') : undefined}
        hint={t('finance.payments.rejectReasonHint')}
      >
        {(props) => (
          <Input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            {...props}
          />
        )}
      </Field>
    </FormSheet>
  )
}
